import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Check, X, ChevronDown, ChevronUp, Loader2, Users, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const LEVELS = [
  { key: "forte", color: "#10b981" },
  { key: "pertinente", color: "#0ea5e9" },
  { key: "possible", color: "#94a3b8" },
];
const LVL_COLOR = { forte: "#10b981", pertinente: "#0ea5e9", possible: "#94a3b8" };

// Panneau « Habitants recommandés » affiché dans le Planning quand une activité du catalogue est choisie.
// Deux sections : (1) Prioritaires — objectif PP lié ; (2) Autres — potentiellement intéressés.
// Les décisions alimentent la même liste que la vue dédiée (activity_key = catalog_id).
export default function PlanningRecoPanel({ catalogId, objectives = [], activityName = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [reco, setReco] = useState(null);
  const [picks, setPicks] = useState({});
  const [loading, setLoading] = useState(false);
  const [maisonId, setMaisonId] = useState("");

  const objKey = JSON.stringify((objectives || []).map((o) => (o || "").trim()).filter(Boolean));
  useEffect(() => {
    const hasObjs = JSON.parse(objKey).length > 0;
    if (!catalogId && !hasObjs) { setReco(null); return; }
    let alive = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/pp/recommendations", { catalog_id: catalogId || null, objectives: JSON.parse(objKey), activity_name: activityName || "" });
        if (!alive) return;
        setReco(data); setMaisonId(data.maison_id || "");
        if (catalogId) {
          const pk = await api.get("/pp/recommendations/picks", { params: { activity_key: catalogId, maison_id: data.maison_id } });
          const pm = {}; (pk.data.picks || []).forEach((p) => { pm[p.resident_id] = p.decision; });
          if (alive) setPicks(pm);
        }
      } catch { if (alive) setReco(null); }
      if (alive) setLoading(false);
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [catalogId, objKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (rid, decision) => {
    if (!catalogId) { setPicks((p) => ({ ...p, [rid]: decision })); return; }
    try {
      await api.post("/pp/recommendations/picks", { activity_key: catalogId, maison_id: maisonId, resident_id: rid, decision });
      setPicks((p) => ({ ...p, [rid]: decision }));
      toast.success(t(decision === "add" ? "pp.reco.added" : "pp.reco.skipped"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const ActionButtons = ({ rid }) => {
    const decided = picks[rid];
    return (
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={() => decide(rid, "add")} data-testid={`pl-reco-add-${rid}`}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${decided === "add" ? "bg-emerald-500 text-white" : "bg-muted hover:bg-emerald-500/20"}`}>
          <Check className="h-3.5 w-3.5" />{decided === "add" ? t("pp.reco.added") : t("pp.reco.add")}
        </button>
        <button type="button" onClick={() => decide(rid, "skip")} data-testid={`pl-reco-skip-${rid}`}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${decided === "skip" ? "bg-red-500 text-white" : "bg-muted hover:bg-red-500/20"}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  if (!catalogId && !(objectives || []).some((o) => (o || "").trim())) return null;
  if (loading) return <div className="mt-4 rounded-lg border p-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> …</div>;
  if (!reco) return null;
  const priority = reco.recommendations || [];
  const others = reco.others || [];
  if (priority.length === 0 && others.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border p-3 bg-amber-500/5" data-testid="pl-reco-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600">
          <Sparkles className="h-4 w-4" />{t("pp.ppSection")} — {t("pp.reco.title")}
          <span className="text-xs font-normal text-muted-foreground">({priority.length + others.length})</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          {/* Section 1 — Prioritaires (objectif PP lié) */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-bold uppercase tracking-wide text-emerald-600">{t("pp.reco.priorityGroup")}</span>
              <span className="text-xs text-muted-foreground">({priority.length})</span>
            </div>
            {priority.length === 0 ? (
              <p className="text-xs text-muted-foreground pl-4">{t("pp.reco.noReco")}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 pl-0.5">
                  {LEVELS.map((l) => (
                    <span key={l.key} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: `${l.color}22`, color: l.color }}>
                      <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />{t(`pp.reco.${l.key}`)} · {reco.counts[l.key] || 0}
                    </span>
                  ))}
                </div>
                {priority.map((r) => {
                  const color = LVL_COLOR[r.level] || LVL_COLOR.possible;
                  const reason = r.reasons?.[0];
                  return (
                    <div key={r.resident_id} className="rounded-md border-l-4 bg-background p-2.5 flex items-start justify-between gap-2" style={{ borderLeftColor: color }} data-testid={`pl-reco-${r.resident_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: color }} />{r.full_name}
                          {r.room && <span className="text-xs text-muted-foreground">· {r.room}</span>}
                          <span className="ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: `${color}22`, color }}>{t(`pp.reco.${r.level}`)}</span>
                        </div>
                        {reason && (
                          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            <div><span className="font-semibold text-foreground/70">{t("pp.reco.objective")} :</span> {reason.objective_title}{reason.domain ? ` · ${reason.domain}` : ""}</div>
                            {reason.actions?.length > 0 && (
                              <div><span className="font-semibold text-foreground/70">{t("pp.reco.actionLbl")} :</span> {reason.actions[0]}</div>
                            )}
                            {reason.matched_keywords?.length > 0 && (
                              <div className="flex flex-wrap gap-1 pt-0.5">
                                {reason.matched_keywords.map((k) => <span key={k} className="rounded-full bg-amber-500/15 text-amber-600 px-1.5 py-0.5 text-[10px]">{k}</span>)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <ActionButtons rid={r.resident_id} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 2 — Autres (potentiellement intéressés) */}
          {others.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Users className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{t("pp.reco.othersGroup")}</span>
                <span className="text-xs text-muted-foreground">({others.length})</span>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2 flex items-start gap-1.5 pl-0.5">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />{t("pp.reco.othersHint")}
              </p>
              <div className="space-y-2">
                {others.map((r) => (
                  <div key={r.resident_id} className="rounded-md border-l-4 border-l-slate-300 bg-background/60 p-2.5 flex items-center justify-between gap-2" data-testid={`pl-reco-other-${r.resident_id}`}>
                    <div className="min-w-0 text-sm font-medium flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full shrink-0 bg-slate-300" />{r.full_name}
                      {r.room && <span className="text-xs text-muted-foreground">· {r.room}</span>}
                    </div>
                    <ActionButtons rid={r.resident_id} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
